import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import api from '../../utils/api';
import { formatIndianCurrency } from '../../utils/currency';
import { 
  Plus, Edit, Trash2, Search, Calendar, Bed, DollarSign, 
  FileText, Settings, ChevronRight, Hotel, Layers,
  Sun, Moon, Snowflake, Leaf, Utensils, Shield, AlertCircle,
  Copy, CheckCircle, X, Save, RotateCcw, Upload, Download
} from 'lucide-react';
import { toast } from 'sonner';

// Season icons map
const SEASON_ICONS = {
  'Peak Season': Sun,
  'Off Season': Moon,
  'Winter': Snowflake,
  'Summer': Sun,
  'Monsoon': Leaf,
  'Default': Calendar
};

// Policy type icons
const POLICY_ICONS = {
  'Cancellation': AlertCircle,
  'Child': Utensils,
  'Extra Bed': Bed,
  'Check-in/Check-out': Calendar,
  'Payment': DollarSign,
  'General': FileText
};

const HotelRateManager = () => {
  // URL params for hotel pre-selection
  const [searchParams] = useSearchParams();
  const preselectedHotelId = searchParams.get('hotel');
  
  // State
  const [hotels, setHotels] = useState([]);
  const [places, setPlaces] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [activeTab, setActiveTab] = useState('seasons');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Masters
  const [masters, setMasters] = useState({
    roomTypes: [],
    bedTypes: [],
    viewTypes: [],
    mealPlans: ['EP', 'CP', 'MAP', 'AP'],
    suppTypes: [],
    features: [],
    ratePerTypes: []
  });
  
  // Hotel data
  const [seasons, setSeasons] = useState([]);
  const [roomConfigs, setRoomConfigs] = useState([]);
  const [rates, setRates] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [policies, setPolicies] = useState([]);
  
  // Dialogs
  const [seasonDialog, setSeasonDialog] = useState(false);
  const [roomConfigDialog, setRoomConfigDialog] = useState(false);
  const [rateDialog, setRateDialog] = useState(false);
  const [supplementDialog, setSupplementDialog] = useState(false);
  const [policyDialog, setPolicyDialog] = useState(false);
  const [mastersDialog, setMastersDialog] = useState(false);
  const [bulkImportDialog, setBulkImportDialog] = useState(false);
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [importType, setImportType] = useState('hotels');
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Editing state
  const [editingSeason, setEditingSeason] = useState(null);
  const [editingRoomConfig, setEditingRoomConfig] = useState(null);
  const [editingRate, setEditingRate] = useState(null);
  const [editingSupplement, setEditingSupplement] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  
  // Form states
  const [seasonForm, setSeasonForm] = useState({
    season_name: '', valid_from: '', valid_to: '', description: '', display_order: 0
  });
  
  const [roomConfigForm, setRoomConfigForm] = useState({
    room_type: '', room_name: '', room_code: '', bed_type: '', view_type: '', size_sqft: '',
    min_occupancy: 1, std_occupancy: 2, max_occupancy: 3, max_adults: 2, max_children: 1,
    extra_bed_allowed: true, features: [], description: '', display_order: 0,
    num_rooms: 0, location: ''  // Added: number of rooms and location/view
  });
  
  // Rate form with support for multiple rate entries
  const [rateForm, setRateForm] = useState({
    room_config_id: '', room_type: '', room_name: '', season_name: '', 
    // Multiple rate entries - each entry has meal_plan, rate_type, and all rates
    rate_entries: [{
      meal_plan: 'CP', rate_type: 'Weekday',
      rate_single_occ: '', rate_double_occ: '', rate_triple_occ: '',
      rate_extra_adult: '', rate_extra_child_bed: '', rate_child_no_bed: '', rate_infant: '',
      room_gst_slab: 0, extra_bed_gst_slab: 0, min_nights: 1, remarks: ''
    }]
  });
  
  // Rate filters and bulk selection state
  const [rateFilters, setRateFilters] = useState({
    room_type: '', season_name: '', meal_plan: '', rate_type: ''
  });
  const [selectedRateIds, setSelectedRateIds] = useState(new Set());
  
  // Copy Season Rates state
  const [copySeasonDialog, setCopySeasonDialog] = useState(false);
  const [copySeasonForm, setCopySeasonForm] = useState({
    source_season: '', target_season: '', percentage_change: 0
  });
  
  const [supplementForm, setSupplementForm] = useState({
    supplement_name: '', supplement_type: '', season_name: '', rate_per: '',
    adult_rate: '', child_rate: '', gst_slab: 18, is_mandatory: false,
    valid_from: '', valid_to: '', remarks: '', display_order: 0
  });
  
  const [policyForm, setPolicyForm] = useState({
    policy_type: '', policy_content: '', effective_from: '', effective_to: '', display_order: 0
  });

  // Fetch initial data
  useEffect(() => {
    fetchHotels();
    fetchPlaces();
    fetchMasters();
  }, []);

  // Fetch hotel data when selected
  useEffect(() => {
    if (selectedHotel) {
      fetchHotelData(selectedHotel.hotel_id);
    }
  }, [selectedHotel]);

  const fetchHotels = async () => {
    try {
      setLoading(true);
      const response = await api.get('/hotels');
      setHotels(response.data);
      
      // Auto-select hotel if URL parameter is present
      if (preselectedHotelId && response.data.length > 0) {
        const hotel = response.data.find(h => h.hotel_id === preselectedHotelId);
        if (hotel) {
          setSelectedHotel(hotel);
        }
      }
    } catch (error) {
      toast.error('Failed to load hotels');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaces = async () => {
    try {
      const response = await api.get('/places');
      setPlaces(response.data);
    } catch (error) {
      console.error('Failed to load places');
    }
  };

  const fetchMasters = async () => {
    try {
      const response = await api.get('/hotel-rates/masters');
      if (response.data) {
        setMasters(prev => ({
          ...prev,
          roomTypes: response.data.roomTypes || prev.roomTypes,
          bedTypes: response.data.bedTypes || prev.bedTypes,
          viewTypes: response.data.viewTypes || prev.viewTypes,
          mealPlans: response.data.mealPlans || prev.mealPlans,
          suppTypes: response.data.suppTypes || prev.suppTypes,
          features: response.data.features || prev.features,
          ratePerTypes: response.data.ratePerTypes || prev.ratePerTypes
        }));
      }
    } catch (error) {
      console.error('Failed to load masters:', error);
    }
  };

  const fetchHotelData = async (hotelId) => {
    try {
      const [seasonsRes, roomConfigsRes, ratesRes, supplementsRes, policiesRes] = await Promise.all([
        api.get(`/hotel-rates/${hotelId}/seasons`),
        api.get(`/hotel-rates/${hotelId}/room-configs`),
        api.get(`/hotel-rates/${hotelId}/rates`),
        api.get(`/hotel-rates/${hotelId}/supplements`),
        api.get(`/hotel-rates/${hotelId}/policies`)
      ]);
      setSeasons(seasonsRes.data);
      setRoomConfigs(roomConfigsRes.data);
      setRates(ratesRes.data);
      setSupplements(supplementsRes.data);
      setPolicies(policiesRes.data);
    } catch (error) {
      toast.error('Failed to load hotel data');
    }
  };

  // Get place info
  const getPlaceInfo = (placeId) => {
    const place = places.find(p => p.place_id === placeId);
    return place ? `${place.place_name}, ${place.state}` : 'Unknown';
  };

  // Filtered hotels
  const filteredHotels = hotels.filter(hotel =>
    hotel.hotel_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getPlaceInfo(hotel.place_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // CRUD handlers for Seasons
  const handleSeasonSubmit = async () => {
    try {
      const data = {
        ...seasonForm,
        display_order: parseInt(seasonForm.display_order) || 0
      };
      if (editingSeason) {
        await api.put(`/hotel-rates/${selectedHotel.hotel_id}/seasons/${editingSeason.season_id}`, data);
        toast.success('Season updated');
      } else {
        await api.post(`/hotel-rates/${selectedHotel.hotel_id}/seasons`, data);
        toast.success('Season created');
      }
      setSeasonDialog(false);
      resetSeasonForm();
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save season');
    }
  };

  const handleDeleteSeason = async (seasonId) => {
    if (!window.confirm('Delete this season?')) return;
    try {
      await api.delete(`/hotel-rates/${selectedHotel.hotel_id}/seasons/${seasonId}`);
      toast.success('Season deleted');
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error('Failed to delete season');
    }
  };

  const resetSeasonForm = () => {
    setSeasonForm({ season_name: '', valid_from: '', valid_to: '', description: '', display_order: 0 });
    setEditingSeason(null);
  };

  const editSeason = (season) => {
    setEditingSeason(season);
    setSeasonForm({
      season_name: season.season_name || '',
      valid_from: season.valid_from || '',
      valid_to: season.valid_to || '',
      description: season.description || '',
      display_order: season.display_order || 0
    });
    setSeasonDialog(true);
  };

  // CRUD handlers for Room Configs
  const handleRoomConfigSubmit = async () => {
    try {
      const data = {
        ...roomConfigForm,
        size_sqft: roomConfigForm.size_sqft ? parseInt(roomConfigForm.size_sqft) : null,
        min_occupancy: parseInt(roomConfigForm.min_occupancy),
        std_occupancy: parseInt(roomConfigForm.std_occupancy),
        max_occupancy: parseInt(roomConfigForm.max_occupancy),
        max_adults: parseInt(roomConfigForm.max_adults),
        max_children: parseInt(roomConfigForm.max_children),
        display_order: parseInt(roomConfigForm.display_order) || 0
      };
      if (editingRoomConfig) {
        await api.put(`/hotel-rates/${selectedHotel.hotel_id}/room-configs/${editingRoomConfig.room_config_id}`, data);
        toast.success('Room configuration updated');
      } else {
        await api.post(`/hotel-rates/${selectedHotel.hotel_id}/room-configs`, data);
        toast.success('Room configuration created');
      }
      setRoomConfigDialog(false);
      resetRoomConfigForm();
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save room configuration');
    }
  };

  const handleDeleteRoomConfig = async (configId) => {
    if (!window.confirm('Delete this room configuration?')) return;
    try {
      await api.delete(`/hotel-rates/${selectedHotel.hotel_id}/room-configs/${configId}`);
      toast.success('Room configuration deleted');
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error('Failed to delete room configuration');
    }
  };

  const resetRoomConfigForm = () => {
    setRoomConfigForm({
      room_type: '', room_name: '', room_code: '', bed_type: '', view_type: '', size_sqft: '',
      min_occupancy: 1, std_occupancy: 2, max_occupancy: 3, max_adults: 2, max_children: 1,
      extra_bed_allowed: true, features: [], description: '', display_order: 0,
      num_rooms: 0, location: ''
    });
    setEditingRoomConfig(null);
  };

  const editRoomConfig = (config) => {
    setEditingRoomConfig(config);
    setRoomConfigForm({
      room_type: config.room_type || '',
      room_name: config.room_name || '',
      room_code: config.room_code || '',
      bed_type: config.bed_type || '',
      view_type: config.view_type || '',
      size_sqft: config.size_sqft?.toString() || '',
      min_occupancy: config.min_occupancy || 1,
      std_occupancy: config.std_occupancy || 2,
      max_occupancy: config.max_occupancy || 3,
      max_adults: config.max_adults || 2,
      max_children: config.max_children || 1,
      extra_bed_allowed: config.extra_bed_allowed !== false,
      features: config.features || [],
      description: config.description || '',
      display_order: config.display_order || 0,
      num_rooms: config.num_rooms || 0,
      location: config.location || ''
    });
    setRoomConfigDialog(true);
  };

  // CRUD handlers for Rates
  const handleRateSubmit = async () => {
    try {
      // When editing, we only update the single rate
      if (editingRate) {
        const entry = rateForm.rate_entries[0];
        const data = {
          room_config_id: rateForm.room_config_id || null,
          room_type: rateForm.room_type || null,
          room_name: rateForm.room_name || null,
          season_name: rateForm.season_name || null,
          meal_plan: entry.meal_plan,
          rate_type: entry.rate_type || 'Weekday',
          rate_single_occ: parseFloat(entry.rate_single_occ) || 0,
          rate_double_occ: parseFloat(entry.rate_double_occ) || 0,
          rate_triple_occ: parseFloat(entry.rate_triple_occ) || 0,
          rate_extra_adult: parseFloat(entry.rate_extra_adult) || 0,
          rate_extra_child_bed: parseFloat(entry.rate_extra_child_bed) || 0,
          rate_child_no_bed: parseFloat(entry.rate_child_no_bed) || 0,
          rate_infant: parseFloat(entry.rate_infant) || 0,
          room_gst_slab: parseFloat(entry.room_gst_slab),
          extra_bed_gst_slab: parseFloat(entry.extra_bed_gst_slab),
          min_nights: parseInt(entry.min_nights) || 1,
          remarks: entry.remarks || ''
        };
        await api.put(`/hotel-rates/${selectedHotel.hotel_id}/rates/${editingRate.hotel_rate_id}`, data);
        toast.success('Rate updated');
      } else {
        // When creating, submit all rate entries
        let createdCount = 0;
        for (const entry of rateForm.rate_entries) {
          if (!entry.rate_double_occ) continue; // Skip entries without double occ rate
          
          const data = {
            room_config_id: rateForm.room_config_id || null,
            room_type: rateForm.room_type || null,
            room_name: rateForm.room_name || null,
            season_name: rateForm.season_name || null,
            meal_plan: entry.meal_plan,
            rate_type: entry.rate_type || 'Weekday',
            rate_single_occ: parseFloat(entry.rate_single_occ) || 0,
            rate_double_occ: parseFloat(entry.rate_double_occ) || 0,
            rate_triple_occ: parseFloat(entry.rate_triple_occ) || 0,
            rate_extra_adult: parseFloat(entry.rate_extra_adult) || 0,
            rate_extra_child_bed: parseFloat(entry.rate_extra_child_bed) || 0,
            rate_child_no_bed: parseFloat(entry.rate_child_no_bed) || 0,
            rate_infant: parseFloat(entry.rate_infant) || 0,
            room_gst_slab: parseFloat(entry.room_gst_slab),
            extra_bed_gst_slab: parseFloat(entry.extra_bed_gst_slab),
            min_nights: parseInt(entry.min_nights) || 1,
            remarks: entry.remarks || ''
          };
          await api.post(`/hotel-rates/${selectedHotel.hotel_id}/rates`, data);
          createdCount++;
        }
        toast.success(`${createdCount} rate(s) created`);
      }
      setRateDialog(false);
      resetRateForm();
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rate');
    }
  };
  
  // Copy Season Rates handler
  const handleCopySeasonRates = async () => {
    if (!copySeasonForm.source_season || !copySeasonForm.target_season) {
      toast.error('Please select both source and target seasons');
      return;
    }
    if (copySeasonForm.source_season === copySeasonForm.target_season) {
      toast.error('Source and target seasons must be different');
      return;
    }
    try {
      const response = await api.post(`/hotel-rates/${selectedHotel.hotel_id}/copy-season-rates`, {
        source_season: copySeasonForm.source_season,
        target_season: copySeasonForm.target_season,
        percentage_change: parseFloat(copySeasonForm.percentage_change) || 0
      });
      toast.success(response.data.message || 'Rates copied successfully');
      setCopySeasonDialog(false);
      setCopySeasonForm({ source_season: '', target_season: '', percentage_change: 0 });
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy rates');
    }
  };

  const handleDeleteRate = async (rateId) => {
    if (!window.confirm('Delete this rate?')) return;
    try {
      await api.delete(`/hotel-rates/${selectedHotel.hotel_id}/rates/${rateId}`);
      toast.success('Rate deleted');
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error('Failed to delete rate');
    }
  };

  const resetRateForm = () => {
    setRateForm({
      room_config_id: '', room_type: '', room_name: '', season_name: '',
      rate_entries: [{
        meal_plan: 'CP', rate_type: 'Weekday',
        rate_single_occ: '', rate_double_occ: '', rate_triple_occ: '',
        rate_extra_adult: '', rate_extra_child_bed: '', rate_child_no_bed: '', rate_infant: '',
        room_gst_slab: 0, extra_bed_gst_slab: 0, min_nights: 1, remarks: ''
      }]
    });
    setEditingRate(null);
  };

  const editRate = (rate) => {
    setEditingRate(rate);
    // When editing, convert single rate to rate_entries format
    setRateForm({
      room_config_id: rate.room_config_id || '',
      room_type: rate.room_type || '',
      room_name: rate.room_name || '',
      season_name: rate.season_name || '',
      rate_entries: [{
        meal_plan: rate.meal_plan || 'CP',
        rate_type: rate.rate_type || 'Weekday',
        rate_single_occ: rate.rate_single_occ?.toString() || '',
        rate_double_occ: rate.rate_double_occ?.toString() || '',
        rate_triple_occ: rate.rate_triple_occ?.toString() || '',
        rate_extra_adult: rate.rate_extra_adult?.toString() || '',
        rate_extra_child_bed: rate.rate_extra_child_bed?.toString() || '',
        rate_child_no_bed: rate.rate_child_no_bed?.toString() || '',
        rate_infant: rate.rate_infant?.toString() || '',
        room_gst_slab: rate.room_gst_slab !== undefined && rate.room_gst_slab !== null ? rate.room_gst_slab : 12,
        extra_bed_gst_slab: rate.extra_bed_gst_slab !== undefined && rate.extra_bed_gst_slab !== null ? rate.extra_bed_gst_slab : 12,
        min_nights: rate.min_nights || 1,
        remarks: rate.remarks || ''
      }]
    });
    setRateDialog(true);
  };

  // Helper to add a new rate entry
  const addRateEntry = () => {
    setRateForm({
      ...rateForm,
      rate_entries: [...rateForm.rate_entries, {
        meal_plan: 'CP', rate_type: 'Weekday',
        rate_single_occ: '', rate_double_occ: '', rate_triple_occ: '',
        rate_extra_adult: '', rate_extra_child_bed: '', rate_child_no_bed: '', rate_infant: '',
        room_gst_slab: 0, extra_bed_gst_slab: 0, min_nights: 1, remarks: ''
      }]
    });
  };

  // Helper to remove a rate entry
  const removeRateEntry = (index) => {
    if (rateForm.rate_entries.length > 1) {
      setRateForm({
        ...rateForm,
        rate_entries: rateForm.rate_entries.filter((_, i) => i !== index)
      });
    }
  };

  // Helper to update a specific rate entry
  const updateRateEntry = (index, field, value) => {
    const newEntries = [...rateForm.rate_entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    setRateForm({ ...rateForm, rate_entries: newEntries });
  };

  // CRUD handlers for Supplements
  const handleSupplementSubmit = async () => {
    try {
      const data = {
        ...supplementForm,
        adult_rate: parseFloat(supplementForm.adult_rate) || 0,
        child_rate: parseFloat(supplementForm.child_rate) || 0,
        gst_slab: parseFloat(supplementForm.gst_slab),
        display_order: parseInt(supplementForm.display_order) || 0
      };
      if (editingSupplement) {
        await api.put(`/hotel-rates/${selectedHotel.hotel_id}/supplements/${editingSupplement.supplement_id}`, data);
        toast.success('Supplement updated');
      } else {
        await api.post(`/hotel-rates/${selectedHotel.hotel_id}/supplements`, data);
        toast.success('Supplement created');
      }
      setSupplementDialog(false);
      resetSupplementForm();
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supplement');
    }
  };

  const handleDeleteSupplement = async (supplementId) => {
    if (!window.confirm('Delete this supplement?')) return;
    try {
      await api.delete(`/hotel-rates/${selectedHotel.hotel_id}/supplements/${supplementId}`);
      toast.success('Supplement deleted');
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error('Failed to delete supplement');
    }
  };

  const resetSupplementForm = () => {
    setSupplementForm({
      supplement_name: '', supplement_type: '', season_name: '', rate_per: '',
      adult_rate: '', child_rate: '', gst_slab: 18, is_mandatory: false,
      valid_from: '', valid_to: '', remarks: '', display_order: 0
    });
    setEditingSupplement(null);
  };

  const editSupplement = (supplement) => {
    setEditingSupplement(supplement);
    setSupplementForm({
      supplement_name: supplement.supplement_name || '',
      supplement_type: supplement.supplement_type || '',
      season_name: supplement.season_name || '',
      rate_per: supplement.rate_per || '',
      adult_rate: supplement.adult_rate?.toString() || '',
      child_rate: supplement.child_rate?.toString() || '',
      gst_slab: supplement.gst_slab || 18,
      is_mandatory: supplement.is_mandatory || false,
      valid_from: supplement.valid_from || '',
      valid_to: supplement.valid_to || '',
      remarks: supplement.remarks || '',
      display_order: supplement.display_order || 0
    });
    setSupplementDialog(true);
  };

  // CRUD handlers for Policies
  const handlePolicySubmit = async () => {
    try {
      const data = {
        ...policyForm,
        display_order: parseInt(policyForm.display_order) || 0
      };
      if (editingPolicy) {
        await api.put(`/hotel-rates/${selectedHotel.hotel_id}/policies/${editingPolicy.policy_id}`, data);
        toast.success('Policy updated');
      } else {
        await api.post(`/hotel-rates/${selectedHotel.hotel_id}/policies`, data);
        toast.success('Policy created');
      }
      setPolicyDialog(false);
      resetPolicyForm();
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Delete this policy?')) return;
    try {
      await api.delete(`/hotel-rates/${selectedHotel.hotel_id}/policies/${policyId}`);
      toast.success('Policy deleted');
      fetchHotelData(selectedHotel.hotel_id);
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const resetPolicyForm = () => {
    setPolicyForm({ policy_type: '', policy_content: '', effective_from: '', effective_to: '', display_order: 0 });
    setEditingPolicy(null);
  };

  const editPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      policy_type: policy.policy_type || '',
      policy_content: policy.policy_content || '',
      effective_from: policy.effective_from || '',
      effective_to: policy.effective_to || '',
      display_order: policy.display_order || 0
    });
    setPolicyDialog(true);
  };

  // Format currency - use centralized utility
  const formatCurrency = (amount) => formatIndianCurrency(amount);

  // Bulk import handlers
  const handleDownloadTemplate = async (type, hotelId = null) => {
    try {
      const endpoints = {
        'unified': '/admin/hotel-rates/template-unified',
        'rates': '/admin/hotel-rates/template-rates-only',
        'rates-only': '/admin/hotel-rates/template-rates-only',
        'hotels': '/admin/hotels/template',
        'seasons': '/admin/hotel-seasons/template',
        'rooms': '/admin/hotel-rooms/template'
      };
      let url = endpoints[type];
      if (type === 'rates-only' && hotelId) {
        url += `?hotel_id=${hotelId}`;
      }
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      // All templates are Excel files
      link.setAttribute('download', `${type}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Template downloaded`);
    } catch (error) {
      console.error('Template download error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to download template: ${errorMessage}`);
    }
  };

  // Validation state
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // For unified import, validate first
    if (importType === 'unified') {
      setIsValidating(true);
      setPendingFile(file);
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await api.post('/admin/bulk-import/hotels-rates/validate', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setValidationResult(response.data);
        
        if (!response.data.valid) {
          toast.error('Validation failed. Please review errors before importing.');
        } else if (response.data.warning_rows > 0) {
          toast.warning(`Validation passed with ${response.data.warning_rows} warnings. Review before importing.`);
        } else {
          toast.success('Validation successful! Ready to import.');
        }
      } catch (error) {
        toast.error('Validation failed: ' + (error.response?.data?.detail || error.message));
        setValidationResult({ valid: false, errors: [error.message] });
      }
      
      setIsValidating(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    // For other import types, proceed directly
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const endpoints = {
        'hotels': '/admin/hotels/upload',
        'rates-only': '/admin/bulk-import/rates-only',
        'seasons': '/admin/hotel-seasons/upload',
        'rooms': '/admin/hotel-rooms/upload'
      };
      const response = await api.post(endpoints[importType], formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      
      // Show appropriate success message based on import type
      if (importType === 'hotels') {
        toast.success(`Imported ${response.data.created_count || 0} hotels`);
        fetchHotels();
      } else if (importType === 'rates-only') {
        const created = response.data.rates_created || 0;
        const updated = response.data.rates_updated || 0;
        toast.success(`Created ${created} rates, Updated ${updated} rates`);
        if (selectedHotel) fetchHotelData(selectedHotel.hotel_id);
      } else {
        toast.success(`Imported ${response.data.created_count || response.data.created?.length || 0} items`);
        if (selectedHotel) fetchHotelData(selectedHotel.hotel_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setImportResult({ success: false, errors: [error.response?.data?.detail || error.message] });
    }
    
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Proceed with import after validation — runs as a background job; we poll its progress
  const [importJob, setImportJob] = useState(null);
  const handleProceedWithImport = async () => {
    if (!pendingFile || !validationResult?.valid || importJob) return;
    const formData = new FormData();
    formData.append('file', pendingFile);
    setImportJob({ status: 'uploading', stage: 'Uploading file', processed: 0, total: 0 });
    try {
      let { data: job } = await api.post('/admin/bulk-import/hotels-rates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (job.already_running) toast.info('An import is already running – showing its progress');
      while (job.status === 'queued' || job.status === 'running') {
        setImportJob(job);
        await new Promise((r) => setTimeout(r, 1500));
        for (let attempt = 0; ; attempt += 1) {
          try {
            ({ data: job } = await api.get(`/admin/bulk-import/jobs/${job.job_id}`));
            break;
          } catch (pollError) {
            if (attempt >= 4 || pollError.response?.status === 404) throw pollError;
            await new Promise((r) => setTimeout(r, 2000));
          }
        }
      }
      if (job.status === 'failed') throw new Error(job.error || 'Import failed');
      setImportResult(job.result);
      toast.success(`Import completed: ${job.result.hotels_created || 0} new hotels, ${job.result.rates_created || 0} new rates, ${job.result.rates_updated || 0} updated`);
      setValidationResult(null);
      setPendingFile(null);
      fetchHotels();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(msg || 'Import failed');
      setImportResult({ success: false, errors: [msg] });
      setValidationResult(null);
    } finally {
      setImportJob(null);
    }
  };

  // Cancel import
  const handleCancelImport = () => {
    setValidationResult(null);
    setPendingFile(null);
  };

  // Export handlers
  const handleExport = async (type) => {
    if (!selectedHotel) {
      toast.error('Please select a hotel first');
      return;
    }
    
    try {
      const endpoints = {
        rates: `/admin/hotel-rates/export/${selectedHotel.hotel_id}`,
        seasons: `/admin/hotel-seasons/export/${selectedHotel.hotel_id}`,
        rooms: `/admin/hotel-rooms/export/${selectedHotel.hotel_id}`
      };
      
      const response = await api.get(endpoints[type], { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedHotel.hotel_name.replace(/\s+/g, '_')}_${type}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} exported successfully`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Delete All Hotels and Rates
  const handleDeleteAll = async () => {
    if (deleteConfirmText !== 'DELETE ALL') {
      toast.error('Please type "DELETE ALL" to confirm');
      return;
    }
    
    setDeleteLoading(true);
    try {
      const response = await api.post('/admin/delete-all-hotels-rates', {
        confirmation: 'DELETE ALL'
      });
      
      const { deleted } = response.data;
      toast.success(
        `Deleted ${deleted.hotels} hotels, ${deleted.rates} rates, ${deleted.room_configs} room configs, ${deleted.seasons} seasons`
      );
      
      // Reset state
      setDeleteAllDialog(false);
      setDeleteConfirmText('');
      setSelectedHotel(null);
      setHotels([]);
      setSeasons([]);
      setRoomConfigs([]);
      setRates([]);
      setSupplements([]);
      setPolicies([]);
      
      // Refresh hotels list
      fetchHotels();
    } catch (error) {
      console.error('Delete all error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete hotel data');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="hotel-rate-manager-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hotel Rate Management</h1>
            <p className="text-sm text-gray-600">Manage seasons, room configurations, rates, supplements and policies</p>
          </div>
          <div className="flex gap-2">
            {/* Export Button */}
            {selectedHotel && (
              <Select onValueChange={handleExport}>
                <SelectTrigger className="w-[140px]" data-testid="export-select">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rates">Export Rates</SelectItem>
                  <SelectItem value="seasons">Export Seasons</SelectItem>
                  <SelectItem value="rooms">Export Rooms</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            <Dialog open={bulkImportDialog} onOpenChange={setBulkImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-import-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Bulk Import Hotels & Rates</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Import Type</Label>
                    <Select value={importType} onValueChange={(v) => { setImportType(v); setValidationResult(null); setPendingFile(null); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hotels">Hotels Only</SelectItem>
                        <SelectItem value="unified">Hotels + Rates (Unified)</SelectItem>
                        <SelectItem value="rates-only">Rates Only (Update Existing)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Import Type Descriptions */}
                  {importType === 'hotels' && (
                    <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                      <p className="font-medium">Import Hotels Only</p>
                      <p className="text-xs mt-1">Simple template to import hotel details. Creates new places if needed.</p>
                      <p className="text-xs mt-1">Required: place_name, state, hotel_name</p>
                    </div>
                  )}
                  
                  {importType === 'unified' && (
                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                      <p className="font-medium">Import Hotels + Rates (Unified)</p>
                      <p className="text-xs mt-1">Comprehensive template for Locations, Hotels, Room Configs, and Rates. Creates new hotels and rates, and updates rates that already exist.</p>
                      <p className="text-xs mt-1">Required: place_name, state, hotel_name, room_type, room_name, meal_plan, date_from, date_to, rate_type, rate_double_occ</p>
                    </div>
                  )}
                  
                  {importType === 'rates-only' && (
                    <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                      <p className="font-medium">Import Rates Only (Update Existing)</p>
                      <p className="text-xs mt-1">Update rates for existing hotels. Matches by Hotel Name + Room Name.</p>
                      <p className="text-xs mt-1">Required: hotel_name, room_name, meal_plan, date_from, date_to, rate_type, rate_double_occ</p>
                    </div>
                  )}
                  
                  {/* Template Download Buttons */}
                  <div className="flex flex-col gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate(importType)}>
                      <Download className="w-4 h-4 mr-2" />
                      Download {importType === 'hotels' ? 'Hotels' : importType === 'unified' ? 'Unified' : importType === 'rates-only' ? 'Rates Only' : importType.charAt(0).toUpperCase() + importType.slice(1)} Template
                    </Button>
                  </div>
                  
                  {/* Show file upload only if no validation in progress */}
                  {!validationResult && (
                    <div>
                      <Label>Upload File {importType === 'unified' ? '(Step 1: Validate)' : ''}</Label>
                      <Input 
                        type="file" 
                        accept=".xlsx,.xls,.csv"
                        ref={fileInputRef}
                        onChange={handleBulkImport}
                        className="mt-1"
                        disabled={isValidating}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Accepts Excel (.xlsx, .xls) or CSV (.csv) files
                      </p>
                      {isValidating && (
                        <div className="flex items-center gap-2 mt-2 text-blue-600">
                          <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                          Validating file...
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Validation Results */}
                  {validationResult && (
                    <div className={`p-4 rounded-lg border ${validationResult.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className={`font-medium ${validationResult.valid ? 'text-green-700' : 'text-red-700'}`}>
                          {validationResult.valid ? 'Validation Passed' : 'Validation Failed'}
                        </h4>
                        <span className="text-sm text-gray-500">
                          {validationResult.valid_rows}/{validationResult.total_rows} valid rows
                        </span>
                      </div>
                      
                      {/* Summary */}
                      {validationResult.summary && (
                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                          <div className="bg-white/50 p-2 rounded">
                            <span className="text-gray-500">New Hotels:</span>
                            <span className="ml-2 font-medium text-blue-600">{validationResult.summary.total_new_hotels}</span>
                          </div>
                          <div className="bg-white/50 p-2 rounded">
                            <span className="text-gray-500">Existing Hotels:</span>
                            <span className="ml-2 font-medium text-green-600">{validationResult.summary.total_existing_hotels}</span>
                          </div>
                          {validationResult.summary.new_hotels_to_create?.length > 0 && (
                            <div className="col-span-2 text-xs text-gray-600">
                              New hotels: {validationResult.summary.new_hotels_to_create.join(', ')}
                              {validationResult.summary.total_new_hotels > 10 && ` (+${validationResult.summary.total_new_hotels - 10} more)`}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Errors */}
                      {validationResult.errors?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-red-700 font-medium text-sm mb-1">Errors ({validationResult.error_rows}):</p>
                          <div className="max-h-32 overflow-y-auto bg-white/50 rounded p-2">
                            <ul className="text-xs space-y-1">
                              {validationResult.errors.slice(0, 10).map((err, i) => (
                                <li key={i} className="text-red-600">
                                  {err.row ? (
                                    <span><strong>Row {err.row}:</strong> {err.errors?.join(', ')}</span>
                                  ) : (
                                    <span>{typeof err === 'string' ? err : JSON.stringify(err)}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                            {validationResult.error_rows > 10 && (
                              <p className="text-xs text-gray-500 mt-1">...and {validationResult.error_rows - 10} more errors</p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Warnings */}
                      {validationResult.warnings?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-amber-700 font-medium text-sm mb-1">Warnings ({validationResult.warning_rows}):</p>
                          <div className="max-h-24 overflow-y-auto bg-white/50 rounded p-2">
                            <ul className="text-xs space-y-1">
                              {validationResult.warnings.slice(0, 5).map((warn, i) => (
                                <li key={i} className="text-amber-600">
                                  <strong>Row {warn.row}:</strong> {warn.warnings?.join(', ')}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                      
                      {importJob && (
                        <div className="mt-3 space-y-1" data-testid="import-progress">
                          <div className="flex justify-between text-xs text-gray-700">
                            <span data-testid="import-progress-stage">{importJob.stage}…</span>
                            {importJob.total > 0 && <span>{importJob.processed} / {importJob.total}</span>}
                          </div>
                          <div className="h-2 rounded bg-gray-200 overflow-hidden">
                            <div className="h-full bg-green-600 transition-[width] duration-500"
                              style={{ width: `${importJob.total ? Math.max(5, Math.round((importJob.processed / importJob.total) * 100)) : 5}%` }} />
                          </div>
                          <p className="text-[11px] text-gray-500">Please keep this window open. Another import can't start until this one finishes.</p>
                        </div>
                      )}
                      {/* Action Buttons */}
                      <div className="flex gap-2 mt-3">
                        {validationResult.valid && (
                          <Button 
                            onClick={handleProceedWithImport} 
                            disabled={!!importJob}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="proceed-import-btn"
                          >
                            {importJob ? 'Importing…' : 'Proceed with Import'}
                          </Button>
                        )}
                        <Button variant="outline" onClick={handleCancelImport} disabled={!!importJob}>
                          {validationResult.valid ? 'Cancel' : 'Try Another File'}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Import Results (after successful import) */}
                  {importResult && !validationResult && (
                    <div className={`p-3 rounded-lg text-sm ${importResult.success !== false ? 'bg-green-50' : 'bg-red-50'}`}>
                      {importResult.success !== false ? (
                        <div>
                          <p className="font-medium text-green-700" data-testid="import-completed">Import Completed</p>
                          {importResult.rates_updated > 0 && <p>Rates Updated: {importResult.rates_updated}</p>}
                          {importResult.duplicates_merged > 0 && <p>Duplicate Hotels Merged: {importResult.duplicates_merged}</p>}
                          {importResult.hotels_created > 0 && <p>Hotels Created: {importResult.hotels_created}</p>}
                          {importResult.rates_created > 0 && <p>Rates Created: {importResult.rates_created}</p>}
                          {importResult.created > 0 && <p>Created: {importResult.created}</p>}
                          {importResult.created_count > 0 && <p>Created: {importResult.created_count}</p>}
                          {importResult.errors?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-yellow-700">Warnings:</p>
                              <ul className="list-disc list-inside text-xs">
                                {importResult.errors.slice(0, 5).map((err, i) => (
                                  <li key={i}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <p className="font-medium text-red-700">Import Failed</p>
                          <ul className="list-disc list-inside text-xs">
                            {importResult.errors?.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setBulkImportDialog(false); setImportResult(null); setValidationResult(null); setPendingFile(null); }}>Close</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Delete All Hotels & Rates Button */}
            <Dialog open={deleteAllDialog} onOpenChange={(open) => { setDeleteAllDialog(open); if (!open) setDeleteConfirmText(''); }}>
              <DialogTrigger asChild>
                <Button variant="destructive" data-testid="delete-all-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete All
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-red-600 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Delete All Hotels & Rates
                  </DialogTitle>
                  <DialogDescription className="text-gray-600">
                    This action will permanently delete ALL hotels, rates, room configurations, and seasons for your agency. This cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 font-medium">⚠️ Warning: This will delete:</p>
                    <ul className="text-sm text-red-600 mt-2 space-y-1 list-disc list-inside">
                      <li>All hotels ({hotels.length} hotels)</li>
                      <li>All hotel rates</li>
                      <li>All room configurations</li>
                      <li>All seasons</li>
                    </ul>
                  </div>
                  <div>
                    <Label htmlFor="confirm-delete" className="text-sm font-medium">
                      Type <span className="font-bold text-red-600">DELETE ALL</span> to confirm:
                    </Label>
                    <Input
                      id="confirm-delete"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE ALL"
                      className="mt-2"
                      data-testid="delete-confirm-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setDeleteAllDialog(false); setDeleteConfirmText(''); }}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleDeleteAll}
                    disabled={deleteConfirmText !== 'DELETE ALL' || deleteLoading}
                    data-testid="confirm-delete-all-btn"
                  >
                    {deleteLoading ? 'Deleting...' : 'Delete All Data'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Button variant="outline" onClick={() => setMastersDialog(true)} data-testid="manage-masters-btn">
              <Settings className="w-4 h-4 mr-2" />
              Manage Masters
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Hotel List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Hotel className="w-4 h-4" />
                Hotels
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Search hotels..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  className="pl-8 h-9" 
                />
              </div>
              <ScrollArea className="h-[calc(100vh-280px)]">
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading...</div>
                ) : filteredHotels.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No hotels found</div>
                ) : (
                  <div className="space-y-1">
                    {filteredHotels.map((hotel) => (
                      <div
                        key={hotel.hotel_id}
                        className={`p-2.5 rounded-lg cursor-pointer transition-all border ${
                          selectedHotel?.hotel_id === hotel.hotel_id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-transparent hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedHotel(hotel)}
                        data-testid={`hotel-item-${hotel.hotel_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">{hotel.hotel_name}</p>
                            <p className="text-xs text-gray-500 truncate">{getPlaceInfo(hotel.place_id)}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Main Content */}
          <Card className="lg:col-span-3">
            <CardContent className="p-4">
              {selectedHotel ? (
                <>
                  {/* Hotel Header */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b">
                    <div>
                      <h2 className="text-lg font-bold">{selectedHotel.hotel_name}</h2>
                      <p className="text-sm text-gray-600">{getPlaceInfo(selectedHotel.place_id)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline">{seasons.length} Seasons</Badge>
                      <Badge variant="outline">{roomConfigs.length} Rooms</Badge>
                      <Badge variant="outline">{rates.length} Rates</Badge>
                    </div>
                  </div>

                  {/* Tabs */}
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-5 mb-4">
                      <TabsTrigger value="seasons" className="text-xs" data-testid="tab-seasons">
                        <Calendar className="w-3 h-3 mr-1" />
                        Seasons
                      </TabsTrigger>
                      <TabsTrigger value="rooms" className="text-xs" data-testid="tab-rooms">
                        <Bed className="w-3 h-3 mr-1" />
                        Rooms
                      </TabsTrigger>
                      <TabsTrigger value="rates" className="text-xs" data-testid="tab-rates">
                        <DollarSign className="w-3 h-3 mr-1" />
                        Rates
                      </TabsTrigger>
                      <TabsTrigger value="supplements" className="text-xs" data-testid="tab-supplements">
                        <Utensils className="w-3 h-3 mr-1" />
                        Supplements
                      </TabsTrigger>
                      <TabsTrigger value="policies" className="text-xs" data-testid="tab-policies">
                        <Shield className="w-3 h-3 mr-1" />
                        Policies
                      </TabsTrigger>
                    </TabsList>

                    {/* Seasons Tab */}
                    <TabsContent value="seasons" className="mt-0">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium">Seasons ({seasons.length})</h3>
                        <Dialog open={seasonDialog} onOpenChange={setSeasonDialog}>
                          <DialogTrigger asChild>
                            <Button size="sm" onClick={resetSeasonForm} data-testid="add-season-btn">
                              <Plus className="w-4 h-4 mr-1" />
                              Add Season
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>{editingSeason ? 'Edit Season' : 'Add Season'}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                              <div>
                                <Label>Season Name *</Label>
                                <Input 
                                  value={seasonForm.season_name}
                                  onChange={(e) => setSeasonForm({...seasonForm, season_name: e.target.value})}
                                  placeholder="e.g., Peak Season"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Valid From</Label>
                                  <Input 
                                    type="date"
                                    value={seasonForm.valid_from}
                                    onChange={(e) => setSeasonForm({...seasonForm, valid_from: e.target.value})}
                                  />
                                </div>
                                <div>
                                  <Label>Valid To</Label>
                                  <Input 
                                    type="date"
                                    value={seasonForm.valid_to}
                                    onChange={(e) => setSeasonForm({...seasonForm, valid_to: e.target.value})}
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Description</Label>
                                <Textarea 
                                  value={seasonForm.description}
                                  onChange={(e) => setSeasonForm({...seasonForm, description: e.target.value})}
                                  placeholder="Optional description"
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setSeasonDialog(false)}>Cancel</Button>
                              <Button onClick={handleSeasonSubmit}>{editingSeason ? 'Update' : 'Create'}</Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                      
                      {seasons.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Calendar className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>No seasons configured. Add your first season.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {seasons.map((season) => {
                            const SeasonIcon = SEASON_ICONS[season.season_name] || SEASON_ICONS.Default;
                            return (
                              <div key={season.season_id} className="border rounded-lg p-3 bg-white hover:shadow-sm transition-shadow">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className="p-2 bg-blue-50 rounded-lg">
                                      <SeasonIcon className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium">{season.season_name}</p>
                                      {season.valid_from && season.valid_to && (
                                        <p className="text-xs text-gray-500">
                                          {new Date(season.valid_from).toLocaleDateString()} - {new Date(season.valid_to).toLocaleDateString()}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => editSeason(season)}>
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteSeason(season.season_id)}>
                                      <Trash2 className="w-3 h-3 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                                {season.description && (
                                  <p className="text-xs text-gray-600 mt-2">{season.description}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </TabsContent>

                    {/* Rooms Tab */}
                    <TabsContent value="rooms" className="mt-0">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium">Room Configurations ({roomConfigs.length})</h3>
                        <Dialog open={roomConfigDialog} onOpenChange={setRoomConfigDialog}>
                          <DialogTrigger asChild>
                            <Button size="sm" onClick={resetRoomConfigForm} data-testid="add-room-config-btn">
                              <Plus className="w-4 h-4 mr-1" />
                              Add Room
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-xl">
                            <DialogHeader>
                              <DialogTitle>{editingRoomConfig ? 'Edit Room Configuration' : 'Add Room Configuration'}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                              {/* Room Type & Room Name */}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Room Type *</Label>
                                  <Select value={roomConfigForm.room_type} onValueChange={(v) => setRoomConfigForm({...roomConfigForm, room_type: v})}>
                                    <SelectTrigger><SelectValue placeholder="Select room type" /></SelectTrigger>
                                    <SelectContent>
                                      {masters.roomTypes.map((rt) => (
                                        <SelectItem key={rt} value={rt}>{rt}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Room Name</Label>
                                  <Input 
                                    value={roomConfigForm.room_name}
                                    onChange={(e) => setRoomConfigForm({...roomConfigForm, room_name: e.target.value})}
                                    placeholder="e.g., Deluxe Garden View"
                                  />
                                </div>
                              </div>

                              {/* Bed Type, View Type, Size */}
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <Label>Bed Type</Label>
                                  <Select value={roomConfigForm.bed_type} onValueChange={(v) => setRoomConfigForm({...roomConfigForm, bed_type: v})}>
                                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                      {masters.bedTypes.map((bt) => (
                                        <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>View Type</Label>
                                  <Select value={roomConfigForm.view_type} onValueChange={(v) => setRoomConfigForm({...roomConfigForm, view_type: v})}>
                                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                      {masters.viewTypes.map((vt) => (
                                        <SelectItem key={vt} value={vt}>{vt}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Size (sq ft)</Label>
                                  <Input 
                                    type="number"
                                    value={roomConfigForm.size_sqft}
                                    onChange={(e) => setRoomConfigForm({...roomConfigForm, size_sqft: e.target.value})}
                                  />
                                </div>
                              </div>

                              {/* Number of Rooms and Location/View */}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>No. of Rooms</Label>
                                  <Input 
                                    type="number"
                                    value={roomConfigForm.num_rooms}
                                    onChange={(e) => setRoomConfigForm({...roomConfigForm, num_rooms: parseInt(e.target.value) || 0})}
                                    placeholder="0"
                                  />
                                </div>
                                <div>
                                  <Label>Location / View</Label>
                                  <Input 
                                    value={roomConfigForm.location}
                                    onChange={(e) => setRoomConfigForm({...roomConfigForm, location: e.target.value})}
                                    placeholder="e.g., Sea View, Garden"
                                  />
                                </div>
                              </div>

                              {/* Occupancy Settings */}
                              <Separator />
                              <p className="text-sm font-medium">Occupancy Settings</p>
                              <div className="grid grid-cols-5 gap-2">
                                <div>
                                  <Label className="text-xs">Min</Label>
                                  <Input type="number" value={roomConfigForm.min_occupancy} onChange={(e) => setRoomConfigForm({...roomConfigForm, min_occupancy: parseInt(e.target.value) || 1})} />
                                </div>
                                <div>
                                  <Label className="text-xs">Standard</Label>
                                  <Input type="number" value={roomConfigForm.std_occupancy} onChange={(e) => setRoomConfigForm({...roomConfigForm, std_occupancy: parseInt(e.target.value) || 2})} />
                                </div>
                                <div>
                                  <Label className="text-xs">Max</Label>
                                  <Input type="number" value={roomConfigForm.max_occupancy} onChange={(e) => setRoomConfigForm({...roomConfigForm, max_occupancy: parseInt(e.target.value) || 3})} />
                                </div>
                                <div>
                                  <Label className="text-xs">Max Adults</Label>
                                  <Input type="number" value={roomConfigForm.max_adults} onChange={(e) => setRoomConfigForm({...roomConfigForm, max_adults: parseInt(e.target.value) || 2})} />
                                </div>
                                <div>
                                  <Label className="text-xs">Max Children</Label>
                                  <Input type="number" value={roomConfigForm.max_children} onChange={(e) => setRoomConfigForm({...roomConfigForm, max_children: parseInt(e.target.value) || 1})} />
                                </div>
                              </div>

                              {/* Features */}
                              <Separator />
                              <div>
                                <Label>Features</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {masters.features.map((feature) => (
                                    <label key={feature} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                      <Checkbox
                                        checked={roomConfigForm.features.includes(feature)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            setRoomConfigForm({...roomConfigForm, features: [...roomConfigForm.features, feature]});
                                          } else {
                                            setRoomConfigForm({...roomConfigForm, features: roomConfigForm.features.filter(f => f !== feature)});
                                          }
                                        }}
                                      />
                                      {feature}
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Description */}
                              <div>
                                <Label>Description</Label>
                                <Textarea 
                                  value={roomConfigForm.description}
                                  onChange={(e) => setRoomConfigForm({...roomConfigForm, description: e.target.value})}
                                  placeholder="Room description"
                                  rows={3}
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setRoomConfigDialog(false)}>Cancel</Button>
                              <Button onClick={handleRoomConfigSubmit}>{editingRoomConfig ? 'Update' : 'Create'}</Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                      
                      {roomConfigs.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Bed className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>No room configurations. Add your first room type.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {roomConfigs.map((config) => (
                            <div key={config.room_config_id} className="border rounded-lg p-3 bg-white">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium">{config.room_name || config.room_type}</span>
                                    {config.room_name && config.room_type && (
                                      <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">{config.room_type}</Badge>
                                    )}
                                    {config.room_code && <Badge variant="secondary" className="text-xs">{config.room_code}</Badge>}
                                    {config.bed_type && <Badge variant="outline" className="text-xs">{config.bed_type}</Badge>}
                                    {config.view_type && <Badge variant="outline" className="text-xs">{config.view_type}</Badge>}
                                  </div>
                                  <div className="flex gap-4 text-xs text-gray-600 mt-1">
                                    <span>Occupancy: {config.min_occupancy}-{config.max_occupancy} ({config.std_occupancy} std)</span>
                                    <span>Adults: max {config.max_adults}</span>
                                    <span>Children: max {config.max_children}</span>
                                    {config.size_sqft && <span>{config.size_sqft} sq ft</span>}
                                  </div>
                                  {config.features?.length > 0 && (
                                    <div className="flex gap-1 mt-2 flex-wrap">
                                      {config.features.map((f, i) => (
                                        <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => editRoomConfig(config)}>
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteRoomConfig(config.room_config_id)}>
                                    <Trash2 className="w-3 h-3 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    {/* Rates Tab */}
                    <TabsContent value="rates" className="mt-0">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium">Rates ({rates.length})</h3>
                        <div className="flex gap-2">
                          {/* Copy Season Rates Button */}
                          {seasons.length >= 2 && (
                            <Dialog open={copySeasonDialog} onOpenChange={setCopySeasonDialog}>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="outline" data-testid="copy-season-rates-btn">
                                  <Copy className="w-4 h-4 mr-1" />
                                  Copy Season Rates
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Copy Rates to Another Season</DialogTitle>
                                  <DialogDescription>
                                    Copy all rates from one season to another with an optional price adjustment.
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <Label>Source Season</Label>
                                    <Select 
                                      value={copySeasonForm.source_season} 
                                      onValueChange={(v) => setCopySeasonForm({...copySeasonForm, source_season: v})}
                                    >
                                      <SelectTrigger><SelectValue placeholder="Select source season" /></SelectTrigger>
                                      <SelectContent>
                                        {seasons.map((s) => (
                                          <SelectItem key={s.season_id} value={s.season_name}>{s.season_name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label>Target Season</Label>
                                    <Select 
                                      value={copySeasonForm.target_season} 
                                      onValueChange={(v) => setCopySeasonForm({...copySeasonForm, target_season: v})}
                                    >
                                      <SelectTrigger><SelectValue placeholder="Select target season" /></SelectTrigger>
                                      <SelectContent>
                                        {seasons.filter(s => s.season_name !== copySeasonForm.source_season).map((s) => (
                                          <SelectItem key={s.season_id} value={s.season_name}>{s.season_name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label>Price Adjustment (%)</Label>
                                    <Input 
                                      type="number" 
                                      value={copySeasonForm.percentage_change} 
                                      onChange={(e) => setCopySeasonForm({...copySeasonForm, percentage_change: e.target.value})}
                                      placeholder="e.g., 10 for +10%, -15 for -15%"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      Positive = increase prices, Negative = decrease prices
                                    </p>
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => setCopySeasonDialog(false)}>Cancel</Button>
                                  <Button onClick={handleCopySeasonRates}>Copy Rates</Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          )}
                          {selectedRateIds.size > 0 && (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={async () => {
                                if (!confirm(`Delete ${selectedRateIds.size} selected rates?`)) return;
                                try {
                                  for (const rateId of selectedRateIds) {
                                    await api.delete(`/hotel-rates/${selectedHotel.hotel_id}/rates/${rateId}`);
                                  }
                                  toast.success(`Deleted ${selectedRateIds.size} rates`);
                                  setSelectedRateIds(new Set());
                                  fetchHotelData(selectedHotel.hotel_id);
                                } catch (error) {
                                  toast.error('Failed to delete rates');
                                }
                              }}
                              data-testid="bulk-delete-rates-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete ({selectedRateIds.size})
                            </Button>
                          )}
                          <Dialog open={rateDialog} onOpenChange={setRateDialog}>
                            <DialogTrigger asChild>
                              <Button size="sm" onClick={resetRateForm} data-testid="add-rate-btn">
                                <Plus className="w-4 h-4 mr-1" />
                                Add Rate
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[90vh]">
                            <DialogHeader>
                              <DialogTitle>{editingRate ? 'Edit Rate' : 'Add Rates'}</DialogTitle>
                              <p className="text-sm text-gray-500">
                                {editingRate ? 'Update rate details' : 'Add multiple rates for the same room and season'}
                              </p>
                            </DialogHeader>
                            <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
                              {/* Room Name and Season - Fixed at top */}
                              <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <div>
                                  <Label>Room Name *</Label>
                                  <Select 
                                    value={rateForm.room_name || ''} 
                                    onValueChange={(v) => {
                                      const selectedRoom = roomConfigs.find(rc => rc.room_name === v || (rc.room_name === '' && rc.room_type === v));
                                      setRateForm({
                                        ...rateForm, 
                                        room_name: v,
                                        room_type: selectedRoom?.room_type || '',
                                        room_config_id: selectedRoom?.room_config_id || ''
                                      });
                                    }}
                                    disabled={!!editingRate}
                                  >
                                    <SelectTrigger data-testid="rate-room-name-select">
                                      <SelectValue placeholder="Select room" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {roomConfigs.length === 0 ? (
                                        <SelectItem value="_no_rooms" disabled>No rooms configured - add rooms first</SelectItem>
                                      ) : (
                                        roomConfigs.map((rc) => (
                                          <SelectItem key={rc.room_config_id} value={rc.room_name || rc.room_type}>
                                            {rc.room_name || rc.room_type} {rc.room_name && rc.room_type ? `(${rc.room_type})` : ''}
                                          </SelectItem>
                                        ))
                                      )}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Season</Label>
                                  <Select 
                                    value={rateForm.season_name || "__all__"} 
                                    onValueChange={(v) => setRateForm({...rateForm, season_name: v === "__all__" ? "" : v})}
                                    disabled={!!editingRate}
                                  >
                                    <SelectTrigger><SelectValue placeholder="Select season" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__all__">All Seasons</SelectItem>
                                      {seasons.map((s) => (
                                        <SelectItem key={s.season_id} value={s.season_name}>{s.season_name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>

                              {/* Rate Entries */}
                              <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                  <p className="text-sm font-medium">Rate Entries ({rateForm.rate_entries.length})</p>
                                  {!editingRate && (
                                    <Button type="button" variant="outline" size="sm" onClick={addRateEntry}>
                                      <Plus className="w-3 h-3 mr-1" /> Add Another Rate
                                    </Button>
                                  )}
                                </div>

                                {rateForm.rate_entries.map((entry, index) => (
                                  <div key={index} className="border rounded-lg p-3 bg-white space-y-3">
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm font-medium text-gray-600">Rate #{index + 1}</span>
                                      {!editingRate && rateForm.rate_entries.length > 1 && (
                                        <Button 
                                          type="button" 
                                          variant="ghost" 
                                          size="sm" 
                                          className="h-6 w-6 p-0 text-red-500"
                                          onClick={() => removeRateEntry(index)}
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      )}
                                    </div>
                                    
                                    {/* Meal Plan and Rate Type */}
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <Label className="text-xs">Meal Plan *</Label>
                                        <Select value={entry.meal_plan} onValueChange={(v) => updateRateEntry(index, 'meal_plan', v)}>
                                          <SelectTrigger><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            {masters.mealPlans.map((mp) => (
                                              <SelectItem key={mp} value={mp}>{mp}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label className="text-xs">Rate Type *</Label>
                                        <Select value={entry.rate_type || 'Weekday'} onValueChange={(v) => updateRateEntry(index, 'rate_type', v)}>
                                          <SelectTrigger><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="Weekday">Weekday</SelectItem>
                                            <SelectItem value="Weekend">Weekend</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>

                                    {/* Room Rates */}
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <Label className="text-xs">Single Occ</Label>
                                        <Input type="number" value={entry.rate_single_occ} onChange={(e) => updateRateEntry(index, 'rate_single_occ', e.target.value)} placeholder="0" className="h-8" />
                                      </div>
                                      <div>
                                        <Label className="text-xs">Double Occ *</Label>
                                        <Input type="number" value={entry.rate_double_occ} onChange={(e) => updateRateEntry(index, 'rate_double_occ', e.target.value)} placeholder="0" className="h-8" />
                                      </div>
                                      <div>
                                        <Label className="text-xs">Triple Occ</Label>
                                        <Input type="number" value={entry.rate_triple_occ} onChange={(e) => updateRateEntry(index, 'rate_triple_occ', e.target.value)} placeholder="0" className="h-8" />
                                      </div>
                                    </div>

                                    {/* Extra Person Rates */}
                                    <div className="grid grid-cols-4 gap-2">
                                      <div>
                                        <Label className="text-xs">Extra Adult</Label>
                                        <Input type="number" value={entry.rate_extra_adult} onChange={(e) => updateRateEntry(index, 'rate_extra_adult', e.target.value)} placeholder="0" className="h-8" />
                                      </div>
                                      <div>
                                        <Label className="text-xs">Child+Bed</Label>
                                        <Input type="number" value={entry.rate_extra_child_bed} onChange={(e) => updateRateEntry(index, 'rate_extra_child_bed', e.target.value)} placeholder="0" className="h-8" />
                                      </div>
                                      <div>
                                        <Label className="text-xs">Child NoBed</Label>
                                        <Input type="number" value={entry.rate_child_no_bed} onChange={(e) => updateRateEntry(index, 'rate_child_no_bed', e.target.value)} placeholder="0" className="h-8" />
                                      </div>
                                      <div>
                                        <Label className="text-xs">Infant</Label>
                                        <Input type="number" value={entry.rate_infant} onChange={(e) => updateRateEntry(index, 'rate_infant', e.target.value)} placeholder="0" className="h-8" />
                                      </div>
                                    </div>

                                    {/* GST and Min Nights */}
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <Label className="text-xs">Room GST %</Label>
                                        <Select value={entry.room_gst_slab.toString()} onValueChange={(v) => updateRateEntry(index, 'room_gst_slab', parseFloat(v))}>
                                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            {[0, 5, 12, 18].map((g) => (
                                              <SelectItem key={g} value={g.toString()}>{g}%</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label className="text-xs">Extra Bed GST %</Label>
                                        <Select value={entry.extra_bed_gst_slab.toString()} onValueChange={(v) => updateRateEntry(index, 'extra_bed_gst_slab', parseFloat(v))}>
                                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            {[0, 5, 12, 18].map((g) => (
                                              <SelectItem key={g} value={g.toString()}>{g}%</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label className="text-xs">Min Nights</Label>
                                        <Input type="number" value={entry.min_nights} onChange={(e) => updateRateEntry(index, 'min_nights', parseInt(e.target.value) || 1)} min="1" className="h-8" />
                                      </div>
                                    </div>

                                    {/* Remarks */}
                                    <div>
                                      <Label className="text-xs">Remarks</Label>
                                      <Input value={entry.remarks} onChange={(e) => updateRateEntry(index, 'remarks', e.target.value)} placeholder="Optional notes" className="h-8" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setRateDialog(false)}>Cancel</Button>
                              <Button onClick={handleRateSubmit}>
                                {editingRate ? 'Update' : `Create ${rateForm.rate_entries.length} Rate(s)`}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        </div>
                      </div>
                      
                      {rates.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>No rates configured. Add your first rate.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Rate Filters */}
                          <div className="flex gap-2 flex-wrap bg-gray-50 p-2 rounded">
                            <Select 
                              value={rateFilters.room_type || '__all__'} 
                              onValueChange={(v) => setRateFilters({...rateFilters, room_type: v === '__all__' ? '' : v})}
                            >
                              <SelectTrigger className="w-36 h-8 text-xs">
                                <SelectValue placeholder="Room Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__all__">All Rooms</SelectItem>
                                {[...new Set(rates.map(r => r.room_type || r.room_name).filter(Boolean))].map((rt) => (
                                  <SelectItem key={rt} value={rt}>{rt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select 
                              value={rateFilters.season_name || '__all__'} 
                              onValueChange={(v) => setRateFilters({...rateFilters, season_name: v === '__all__' ? '' : v})}
                            >
                              <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue placeholder="Season" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__all__">All Seasons</SelectItem>
                                {[...new Set(rates.map(r => r.season_name).filter(Boolean))].map((sn) => (
                                  <SelectItem key={sn} value={sn}>{sn}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select 
                              value={rateFilters.meal_plan || '__all__'} 
                              onValueChange={(v) => setRateFilters({...rateFilters, meal_plan: v === '__all__' ? '' : v})}
                            >
                              <SelectTrigger className="w-24 h-8 text-xs">
                                <SelectValue placeholder="Meal" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__all__">All</SelectItem>
                                {masters.mealPlans.map((mp) => (
                                  <SelectItem key={mp} value={mp}>{mp}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select 
                              value={rateFilters.rate_type || '__all__'} 
                              onValueChange={(v) => setRateFilters({...rateFilters, rate_type: v === '__all__' ? '' : v})}
                            >
                              <SelectTrigger className="w-28 h-8 text-xs">
                                <SelectValue placeholder="Rate Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__all__">All Types</SelectItem>
                                <SelectItem value="Weekday">Weekday</SelectItem>
                                <SelectItem value="Weekend">Weekend</SelectItem>
                              </SelectContent>
                            </Select>
                            {(rateFilters.room_type || rateFilters.season_name || rateFilters.meal_plan || rateFilters.rate_type) && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 text-xs"
                                onClick={() => setRateFilters({room_type: '', season_name: '', meal_plan: '', rate_type: ''})}
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="w-8 p-2">
                                    <Checkbox 
                                      checked={selectedRateIds.size > 0 && selectedRateIds.size === rates.filter(r => {
                                        if (rateFilters.room_type && (r.room_type || r.room_name) !== rateFilters.room_type) return false;
                                        if (rateFilters.season_name && r.season_name !== rateFilters.season_name) return false;
                                        if (rateFilters.meal_plan && r.meal_plan !== rateFilters.meal_plan) return false;
                                        if (rateFilters.rate_type && r.rate_type !== rateFilters.rate_type) return false;
                                        return true;
                                      }).length}
                                      onCheckedChange={(checked) => {
                                        const filteredRates = rates.filter(r => {
                                          if (rateFilters.room_type && (r.room_type || r.room_name) !== rateFilters.room_type) return false;
                                          if (rateFilters.season_name && r.season_name !== rateFilters.season_name) return false;
                                          if (rateFilters.meal_plan && r.meal_plan !== rateFilters.meal_plan) return false;
                                          if (rateFilters.rate_type && r.rate_type !== rateFilters.rate_type) return false;
                                          return true;
                                        });
                                        if (checked) {
                                          setSelectedRateIds(new Set(filteredRates.map(r => r.hotel_rate_id)));
                                        } else {
                                          setSelectedRateIds(new Set());
                                        }
                                      }}
                                    />
                                  </th>
                                  <th className="text-left p-2">Room Type</th>
                                  <th className="text-left p-2">Room Name</th>
                                  <th className="text-left p-2">Season</th>
                                  <th className="text-left p-2">Meal</th>
                                  <th className="text-left p-2">Type</th>
                                  <th className="text-right p-2">Single</th>
                                  <th className="text-right p-2">Double</th>
                                  <th className="text-right p-2">Triple</th>
                                  <th className="text-right p-2">Extra Adult</th>
                                  <th className="text-center p-2">GST</th>
                                  <th className="text-center p-2">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rates
                                  .filter(rate => {
                                    if (rateFilters.room_type && (rate.room_type || rate.room_name) !== rateFilters.room_type) return false;
                                    if (rateFilters.season_name && rate.season_name !== rateFilters.season_name) return false;
                                    if (rateFilters.meal_plan && rate.meal_plan !== rateFilters.meal_plan) return false;
                                    if (rateFilters.rate_type && rate.rate_type !== rateFilters.rate_type) return false;
                                    return true;
                                  })
                                  .map((rate) => (
                                    <tr key={rate.hotel_rate_id} className="border-b hover:bg-gray-50">
                                      <td className="p-2">
                                        <Checkbox 
                                          checked={selectedRateIds.has(rate.hotel_rate_id)}
                                          onCheckedChange={(checked) => {
                                            const newSet = new Set(selectedRateIds);
                                            if (checked) {
                                              newSet.add(rate.hotel_rate_id);
                                            } else {
                                              newSet.delete(rate.hotel_rate_id);
                                            }
                                            setSelectedRateIds(newSet);
                                          }}
                                        />
                                      </td>
                                      <td className="p-2 font-medium">{rate.room_type || '-'}</td>
                                      <td className="p-2">{rate.room_name || '-'}</td>
                                      <td className="p-2">{rate.season_name || 'All'}</td>
                                      <td className="p-2"><Badge variant="outline">{rate.meal_plan}</Badge></td>
                                      <td className="p-2">
                                        <Badge variant={rate.rate_type === 'Weekend' ? 'default' : 'secondary'} className="text-xs">
                                          {rate.rate_type || 'Weekday'}
                                        </Badge>
                                      </td>
                                      <td className="p-2 text-right">{formatCurrency(rate.rate_single_occ)}</td>
                                      <td className="p-2 text-right font-medium text-green-600">{formatCurrency(rate.rate_double_occ)}</td>
                                      <td className="p-2 text-right">{formatCurrency(rate.rate_triple_occ)}</td>
                                      <td className="p-2 text-right">{formatCurrency(rate.rate_extra_adult)}</td>
                                      <td className="p-2 text-center">{rate.room_gst_slab !== undefined && rate.room_gst_slab !== null ? rate.room_gst_slab : (rate.gst_slab || 0)}%</td>
                                      <td className="p-2">
                                        <div className="flex justify-center gap-1">
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => editRate(rate)}>
                                            <Edit className="w-3 h-3" />
                                          </Button>
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteRate(rate.hotel_rate_id)}>
                                            <Trash2 className="w-3 h-3 text-red-500" />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </TabsContent>

                    {/* Supplements Tab */}
                    <TabsContent value="supplements" className="mt-0">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium">Supplements ({supplements.length})</h3>
                        <Dialog open={supplementDialog} onOpenChange={setSupplementDialog}>
                          <DialogTrigger asChild>
                            <Button size="sm" onClick={resetSupplementForm} data-testid="add-supplement-btn">
                              <Plus className="w-4 h-4 mr-1" />
                              Add Supplement
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg">
                            <DialogHeader>
                              <DialogTitle>{editingSupplement ? 'Edit Supplement' : 'Add Supplement'}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Supplement Name *</Label>
                                  <Input 
                                    value={supplementForm.supplement_name}
                                    onChange={(e) => setSupplementForm({...supplementForm, supplement_name: e.target.value})}
                                    placeholder="e.g., Gala Dinner"
                                  />
                                </div>
                                <div>
                                  <Label>Type</Label>
                                  <Select value={supplementForm.supplement_type} onValueChange={(v) => setSupplementForm({...supplementForm, supplement_type: v})}>
                                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                                    <SelectContent>
                                      {masters.suppTypes.map((st) => (
                                        <SelectItem key={st} value={st}>{st}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Season</Label>
                                  <Select value={supplementForm.season_name || "__all__"} onValueChange={(v) => setSupplementForm({...supplementForm, season_name: v === "__all__" ? "" : v})}>
                                    <SelectTrigger><SelectValue placeholder="All seasons" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__all__">All Seasons</SelectItem>
                                      {seasons.map((s) => (
                                        <SelectItem key={s.season_id} value={s.season_name}>{s.season_name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Rate Per</Label>
                                  <Select value={supplementForm.rate_per} onValueChange={(v) => setSupplementForm({...supplementForm, rate_per: v})}>
                                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                      {masters.ratePerTypes.map((rpt) => (
                                        <SelectItem key={rpt} value={rpt}>{rpt}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <Label>Adult Rate</Label>
                                  <Input type="number" value={supplementForm.adult_rate} onChange={(e) => setSupplementForm({...supplementForm, adult_rate: e.target.value})} placeholder="0" />
                                </div>
                                <div>
                                  <Label>Child Rate</Label>
                                  <Input type="number" value={supplementForm.child_rate} onChange={(e) => setSupplementForm({...supplementForm, child_rate: e.target.value})} placeholder="0" />
                                </div>
                                <div>
                                  <Label>GST %</Label>
                                  <Select value={supplementForm.gst_slab.toString()} onValueChange={(v) => setSupplementForm({...supplementForm, gst_slab: parseFloat(v)})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {[0, 5, 12, 18].map((g) => (
                                        <SelectItem key={g} value={g.toString()}>{g}%</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Valid From</Label>
                                  <Input type="date" value={supplementForm.valid_from} onChange={(e) => setSupplementForm({...supplementForm, valid_from: e.target.value})} />
                                </div>
                                <div>
                                  <Label>Valid To</Label>
                                  <Input type="date" value={supplementForm.valid_to} onChange={(e) => setSupplementForm({...supplementForm, valid_to: e.target.value})} />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox 
                                  checked={supplementForm.is_mandatory}
                                  onCheckedChange={(checked) => setSupplementForm({...supplementForm, is_mandatory: checked})}
                                />
                                <Label>Mandatory Supplement</Label>
                              </div>
                              <div>
                                <Label>Remarks</Label>
                                <Textarea value={supplementForm.remarks} onChange={(e) => setSupplementForm({...supplementForm, remarks: e.target.value})} placeholder="Optional notes" />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setSupplementDialog(false)}>Cancel</Button>
                              <Button onClick={handleSupplementSubmit}>{editingSupplement ? 'Update' : 'Create'}</Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                      
                      {supplements.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Utensils className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>No supplements configured. Add meal upgrades, festive charges, etc.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {supplements.map((supp) => (
                            <div key={supp.supplement_id} className="border rounded-lg p-3 bg-white">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium">{supp.supplement_name}</span>
                                    {supp.is_mandatory && <Badge variant="destructive" className="text-xs">Mandatory</Badge>}
                                    {supp.supplement_type && <Badge variant="outline" className="text-xs">{supp.supplement_type}</Badge>}
                                    {supp.season_name && <Badge variant="secondary" className="text-xs">{supp.season_name}</Badge>}
                                  </div>
                                  <div className="flex gap-4 text-xs text-gray-600 mt-1">
                                    <span>Adult: {formatCurrency(supp.adult_rate)}</span>
                                    <span>Child: {formatCurrency(supp.child_rate)}</span>
                                    {supp.rate_per && <span>({supp.rate_per})</span>}
                                    <span>GST: {supp.gst_slab}%</span>
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => editSupplement(supp)}>
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteSupplement(supp.supplement_id)}>
                                    <Trash2 className="w-3 h-3 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    {/* Policies Tab */}
                    <TabsContent value="policies" className="mt-0">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium">Policies ({policies.length})</h3>
                        <Dialog open={policyDialog} onOpenChange={setPolicyDialog}>
                          <DialogTrigger asChild>
                            <Button size="sm" onClick={resetPolicyForm} data-testid="add-policy-btn">
                              <Plus className="w-4 h-4 mr-1" />
                              Add Policy
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Add Policy'}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                              <div>
                                <Label>Policy Type *</Label>
                                <Select value={policyForm.policy_type} onValueChange={(v) => setPolicyForm({...policyForm, policy_type: v})}>
                                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                                  <SelectContent>
                                    {['Cancellation', 'Child', 'Extra Bed', 'Check-in/Check-out', 'Payment', 'General'].map((pt) => (
                                      <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label>Policy Content</Label>
                                <Textarea 
                                  value={policyForm.policy_content}
                                  onChange={(e) => setPolicyForm({...policyForm, policy_content: e.target.value})}
                                  placeholder="Enter policy details..."
                                  rows={5}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Effective From</Label>
                                  <Input type="date" value={policyForm.effective_from} onChange={(e) => setPolicyForm({...policyForm, effective_from: e.target.value})} />
                                </div>
                                <div>
                                  <Label>Effective To</Label>
                                  <Input type="date" value={policyForm.effective_to} onChange={(e) => setPolicyForm({...policyForm, effective_to: e.target.value})} />
                                </div>
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setPolicyDialog(false)}>Cancel</Button>
                              <Button onClick={handlePolicySubmit}>{editingPolicy ? 'Update' : 'Create'}</Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                      
                      {policies.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Shield className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>No policies configured. Add cancellation, child policies, etc.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {policies.map((policy) => {
                            const PolicyIcon = POLICY_ICONS[policy.policy_type] || POLICY_ICONS.General;
                            return (
                              <div key={policy.policy_id} className="border rounded-lg p-3 bg-white">
                                <div className="flex items-start justify-between">
                                  <div className="flex gap-3 flex-1">
                                    <div className="p-2 bg-gray-50 rounded-lg flex-shrink-0">
                                      <PolicyIcon className="w-4 h-4 text-gray-600" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{policy.policy_type}</span>
                                        {policy.effective_from && policy.effective_to && (
                                          <span className="text-xs text-gray-500">
                                            ({new Date(policy.effective_from).toLocaleDateString()} - {new Date(policy.effective_to).toLocaleDateString()})
                                          </span>
                                        )}
                                      </div>
                                      {policy.policy_content && (
                                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{policy.policy_content}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => editPolicy(policy)}>
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeletePolicy(policy.policy_id)}>
                                      <Trash2 className="w-3 h-3 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-400">
                  <div className="text-center">
                    <Layers className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Select a hotel to manage rates</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Masters Dialog */}
        <Dialog open={mastersDialog} onOpenChange={setMastersDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Manage Master Data</DialogTitle>
              <DialogDescription>
                Add, edit, or delete master values. Changes will apply across all hotels.
              </DialogDescription>
            </DialogHeader>
            <Tabs defaultValue="roomTypes" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="flex flex-wrap h-auto gap-1">
                <TabsTrigger value="roomTypes" className="text-xs">Room Types</TabsTrigger>
                <TabsTrigger value="mealPlans" className="text-xs">Meal Plans</TabsTrigger>
                <TabsTrigger value="bedTypes" className="text-xs">Bed Types</TabsTrigger>
                <TabsTrigger value="viewTypes" className="text-xs">View Types</TabsTrigger>
                <TabsTrigger value="features" className="text-xs">Features</TabsTrigger>
                <TabsTrigger value="suppTypes" className="text-xs">Supplement Types</TabsTrigger>
                <TabsTrigger value="ratePerTypes" className="text-xs">Rate Per Types</TabsTrigger>
              </TabsList>
              {Object.entries(masters).map(([key, values]) => (
                <TabsContent key={key} value={key} className="flex-1 overflow-auto mt-4">
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input 
                        placeholder={`Add new ${key.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}...`}
                        id={`new-${key}`}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            try {
                              await api.post('/hotel-rates/masters', { 
                                category: key, 
                                value: e.target.value.trim() 
                              });
                              fetchMasters();
                              e.target.value = '';
                              toast.success('Added successfully');
                            } catch (error) {
                              toast.error('Failed to add');
                            }
                          }
                        }}
                      />
                      <Button size="sm" onClick={async () => {
                        const input = document.getElementById(`new-${key}`);
                        if (input && input.value.trim()) {
                          try {
                            await api.post('/hotel-rates/masters', { 
                              category: key, 
                              value: input.value.trim() 
                            });
                            fetchMasters();
                            input.value = '';
                            toast.success('Added successfully');
                          } catch (error) {
                            toast.error('Failed to add');
                          }
                        }
                      }}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                      {values.length === 0 ? (
                        <p className="p-4 text-sm text-gray-500 text-center">No values yet. Add one above.</p>
                      ) : (
                        values.map((v, i) => (
                          <div key={i} className="flex items-center justify-between p-2 hover:bg-gray-50">
                            <span className="text-sm">{v}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                              onClick={async () => {
                                if (!confirm(`Delete "${v}"? This may affect existing data.`)) return;
                                try {
                                  await api.delete('/hotel-rates/masters', { 
                                    data: { category: key, value: v } 
                                  });
                                  fetchMasters();
                                  toast.success('Deleted successfully');
                                } catch (error) {
                                  toast.error('Failed to delete');
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setMastersDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default HotelRateManager;
